/**
 * Segment contracts — slice 2, "segments"; slice 50, "cohorts".
 *
 * THERE ARE NOW TWO KINDS OF SEGMENT, AND THEY DIFFER IN WHAT MEMBERSHIP IS.
 *
 * A **`static`** segment is a named union of DATASETS. It owns no contacts of
 * its own: it references the datasets, so re-importing a dataset changes every
 * segment that includes it. That is the correct behaviour and it is the reason
 * no rows are ever copied into a static segment. Everything slice 2 said about
 * this kind is unchanged.
 *
 * A **`cohort`** is a SNAPSHOT of the people one Overview count counted, at one
 * instant. It owns `segment_contacts` rows — one for every `contacts` row of
 * every person in the population — because a set of PEOPLE cannot be expressed
 * as a set of datasets at all, which is what stopped slice 32's *"1,284 people
 * have not heard from you in 90 days"* from becoming an audience. So the claim
 * this header used to make about every segment — *"it owns no contacts of its
 * own"* — is now true of one kind and false of the other, and this is the
 * paragraph that says which.
 *
 * Nothing is COPIED into a cohort either. A member row is a reference to a
 * `contacts` row, cascading from it, so deleting an upload shrinks the cohort
 * and a cohort whose people are all gone reports zeros. That is why there is
 * still no stored count anywhere below: a saved 1,284 beside a real 0 is the
 * failure this repository has shipped twice.
 *
 * Two things this file exists to pin down:
 *
 * - **Three counts, never one.** See {@link segmentCountsSchema}. The two real
 *   lists we import hold 1,287 contacts but only 1,278 unique recipients; a
 *   segment that advertises 1,287 and then sends 1,278 messages looks broken to
 *   the customer, so a segment reports the union size, the de-duplicated
 *   recipient count, and the **distinct addresses** it can reach on each
 *   channel. Only the first of those is a row count; see
 *   {@link segmentCountsSchema} for why the other three are not.
 * - **Counts are computed on read, never stored.** A cached count drifts the
 *   moment a dataset is re-imported. There is deliberately no `contactCount`
 *   column on `segments`, and nothing here should grow one.
 *
 * A **`rule`** segment (slice 67) is DYNAMIC: a rule tree over the field
 * catalogue (`./rules.js`, `./fields.js`), whose members — `segment_people`, by
 * PERSON — are refreshed whenever the data could have changed who matches, with
 * every entry and exit recorded (`segment_membership_events`). It arrived, as the
 * superseded paragraph here promised, as a THIRD `type` beside the other two and
 * not as a replacement for `cohort`: every population a cohort can hold is
 * defined by the absence of a message, so sending to it destroys it, and a rule
 * re-evaluated when a scheduled campaign materialised would find nobody. Its
 * counts are still computed on read, over its members. The superseded position
 * — "no filter, operator, predicate or jsonb rule shape belongs in this file" —
 * holds in a narrower form: the rule GRAMMAR lives in `./rules.js`; this file
 * only carries it on a segment and defines the builder's preview and the
 * membership timeline.
 */
import { z } from 'zod';

import {
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  sortDirectionSchema,
  uuidSchema,
} from '../common.js';
import { datasetStatusSchema, segmentTypeSchema } from '../enums.js';
/*
 * ONE ARROW, UPWARDS ONLY. This file reads `./opportunity.js` for the three
 * counts that are a population of people; that file imports nothing from here,
 * so the two do not close a cycle. The save endpoint's request and response
 * shapes therefore live HERE rather than beside the sentences in
 * `opportunity.ts`, because their payload is a segment and putting them there
 * would need `segmentSchema` back — which is the arrow that would close it.
 * Every SENTENCE about a cohort is still defined in `opportunity.ts`.
 */
import { opportunityKindSchema, saveableOpportunityKindSchema } from './opportunity.js';
import { ruleIdSchema, ruleTreeSchema } from './rules.js';

/* -------------------------------------------------------------------------- */
/* Limits                                                                      */
/* -------------------------------------------------------------------------- */

export const MAX_SEGMENT_NAME_LENGTH = 120;

export const MAX_SEGMENT_DESCRIPTION_LENGTH = 500;

/**
 * Datasets one segment may combine. Generous on purpose — this is a guard
 * against a malformed request, not a product limit. The union query is a single
 * `dataset_id IN (...)`, so the real cost is in the contacts, not the members.
 */
export const MAX_SEGMENT_DATASETS = 100;

/* -------------------------------------------------------------------------- */
/* Fields                                                                      */
/* -------------------------------------------------------------------------- */

export const segmentNameSchema = z.string().trim().min(1).max(MAX_SEGMENT_NAME_LENGTH);

/**
 * Optional prose. A cleared textarea arrives as `''` and is stored as NULL —
 * the database carries a `segments_description_not_blank` CHECK that refuses a
 * blank string, so the normalisation has to happen here rather than at the edge
 * of each caller.
 */
export const segmentDescriptionSchema = z
  .string()
  .trim()
  .max(MAX_SEGMENT_DESCRIPTION_LENGTH)
  .transform((value) => (value === '' ? null : value))
  .nullable();

/**
 * The datasets a segment is the union of.
 *
 * An **empty list is valid**: a segment you have just created and not yet
 * filled is a normal state, and it reports zeros rather than failing. Ids must
 * be distinct — `segment_datasets` is unique on `(segment_id, dataset_id)`, so
 * a repeated id would be a 23505 from the database instead of a field error.
 */
export const segmentDatasetIdsSchema = z
  .array(uuidSchema)
  .max(MAX_SEGMENT_DATASETS)
  .refine(
    (ids) => new Set(ids).size === ids.length,
    'A dataset can only be included once in a segment',
  );

/* -------------------------------------------------------------------------- */
/* What makes a cohort a cohort — slice 50                                     */
/* -------------------------------------------------------------------------- */

/**
 * The provenance of a cohort: which count produced it, and when it was counted.
 *
 * `null` on a static segment, and **never an empty object**. A `{}` would be a
 * cohort that cannot say where it came from, which the database refuses too:
 * `segments_cohort_kind_pairs_with_type` and
 * `segments_cohort_counted_at_pairs_with_type` state the rule in both
 * directions, so a cohort without a kind and a static segment carrying one are
 * equally impossible.
 *
 * `countedAt` is the instant the SAVE's own transaction stamped — never a
 * timestamp from the client, and never the card's older `generatedAt`, because a
 * read that did not produce the snapshot may not date it. It is on the wire
 * because the snapshot's AGE is the one thing a person about to send needs to
 * know and cannot work out: a three-month-old cohort is a list of who was
 * dormant three months ago. The screen draws it as a date beside the type chip,
 * in the register `OPPORTUNITY_COPY.source` already uses — a label, not a
 * sentence.
 *
 * **There is no member count here**, and that is the slice's central guarantee
 * rather than an omission: `counts.uniqueRecipients` below IS the number the
 * card printed, because a cohort holds every `contacts` row of every person it
 * counted and `uniqueRecipients` is `count(distinct person_id)` over them
 * (since slice 58). A second, stored figure could only drift from it.
 *
 * The **fingerprint** the database uses to recognise the same population twice
 * is deliberately absent from the wire. It is a sha256 over the addresses
 * themselves; it adds no exposure the database does not already have, and it
 * must never become a URL parameter, a cache key or a log line.
 */
export const segmentCohortSchema = z
  .object({
    kind: saveableOpportunityKindSchema,
    countedAt: isoDateTimeSchema,
  })
  .strict();
export type SegmentCohort = z.infer<typeof segmentCohortSchema>;

/* -------------------------------------------------------------------------- */
/* Counts — the heart of this slice                                            */
/* -------------------------------------------------------------------------- */

/**
 * What a segment actually contains, computed on every read.
 *
 * The canonical query — every consumer must produce exactly these numbers, so
 * the definition lives here rather than in the API. As of slice 58:
 *
 * ```sql
 * SELECT
 *   count(*)                                              AS contacts,
 *   count(DISTINCT c.person_id)                           AS unique_recipients,
 *   count(DISTINCT c.email)                               AS email_reachable,
 *   count(DISTINCT c.phone_e164) FILTER (WHERE rank = 1)  AS phone_reachable
 * FROM (
 *   SELECT c.*, <representativeRank on phone_e164> AS rank   -- segment-membership.ts
 *   FROM contacts c
 *   WHERE c.dataset_id IN (SELECT sd.dataset_id
 *                            FROM segment_datasets sd
 *                           WHERE sd.segment_id = $1)
 * ) c;
 * ```
 *
 * Until slice 58 `unique_recipients` was `count(DISTINCT coalesce(email,
 * phone_e164))` and `phone_reachable` every row's distinct number; the first
 * counted one customer as two whenever two uploads carried different addresses
 * of theirs, and the second advertised a person's second phone, which the send
 * never uses.
 *
 * The `FROM` above is the static kind's half of one member relation. Since
 * slice 50 the API reads both halves — `segment_datasets ⨝ contacts` UNION ALL
 * `segment_contacts ⨝ contacts` — and the four numbers are computed over the
 * result whichever kind the segment is. `UNION ALL` rather than `UNION`: the two
 * limbs are provably disjoint for any one segment, because a `segment_type`
 * column on each membership table is part of a composite foreign key into
 * `segments (id, tenant_id, type)`, and `0037_segment_contacts.sql` refuses both
 * crossings by probe. A distinct pass would be cost with no property bought.
 *
 * - `contacts` — rows in the union. `segment_datasets` is unique on
 *   `(segment_id, dataset_id)` and `segment_contacts` on `(segment_id,
 *   contact_id)`, so nothing can be counted twice on either path. **This is
 *   the only row count of the four.**
 * - `uniqueRecipients` — people: distinct `person_id` since slice 58, which is
 *   deliberately the **same unit the send path reaches once**. Materialisation
 *   writes at most one recipient row per person — exactly one on email; on
 *   WhatsApp a person whose every number is already used by an earlier person
 *   gets none (rule 3b), which is why `phoneReachable`, not this, is the
 *   WhatsApp figure. It is why the count is plural: 1,287 contacts across our
 *   two real lists are 1,278 people.
 * - `emailReachable` / `phoneReachable` — how many **distinct addresses** of
 *   that kind a campaign would use. An email has one owner, so `emailReachable`
 *   is every distinct email in the segment; `phoneReachable` counts each
 *   person's own number first (their representative row), so a person whose
 *   uploads carry two phones is one number, not two.
 *
 * **The last two were once row counts, and that was wrong.** The superseded
 * position, kept here because it is the reading somebody will arrive at again:
 * `count(*) FILTER (WHERE c.email IS NOT NULL)` answers "how much of this
 * segment is addressable on that channel", it sums to `contacts` when nobody
 * carries both, and it is a tidier arithmetic. It is also not what gets sent.
 * The first real import has 531 rows carrying an email and only **526** distinct
 * addresses; a segment advertising 531 and delivering 526 is the broken-looking
 * gap this whole shape exists to close. Two things followed from the change and
 * both are load-bearing: the four numbers are no longer all the same unit, so
 * `emailReachable + phoneReachable − contacts` is not arithmetic and no longer
 * appears anywhere; and `readSegmentTotals` in the API — the canonical
 * implementation of the query above — has used `count(distinct …)` since the
 * slice 2 review, so this comment described something no code did. Slice 14's
 * `./analytics.js` takes the lesson further and gives a distinct-address count
 * its own *type*, so the two units cannot be mixed again by anybody who never
 * reads this paragraph.
 *
 * A segment with no datasets — or a cohort whose every contact has since been
 * deleted — reports {@link EMPTY_SEGMENT_COUNTS}: all zeros, not an error and
 * not a blank. A cohort saved at 1,284 whose upload is then deleted reads `0`,
 * computed, drawn as a zero. That is the whole reason no count is stored.
 */
export const segmentCountsSchema = z.object({
  /** Rows in the union of the segment's datasets. The only row count here. */
  contacts: z.number().int().min(0),
  /** `count(distinct person_id)` — people, not rows (slice 58). */
  uniqueRecipients: z.number().int().min(0),
  /** `count(distinct email)` — distinct addresses, not rows carrying one. */
  emailReachable: z.number().int().min(0),
  /** Distinct numbers over each person's representative row — what a WhatsApp send would use. */
  phoneReachable: z.number().int().min(0),
});
export type SegmentCounts = z.infer<typeof segmentCountsSchema>;

/**
 * The counts of a segment with nothing in it: a static segment with no datasets
 * or only empty ones, or a cohort whose members have all been deleted.
 *
 * Zeros, and a real zero — this is a measurement that came back as none, not a
 * measurement nobody made. Nothing in this package has an "unknown" shape for a
 * segment's counts, because a segment's counts are always computable.
 */
export const EMPTY_SEGMENT_COUNTS: SegmentCounts = {
  contacts: 0,
  uniqueRecipients: 0,
  emailReachable: 0,
  phoneReachable: 0,
};

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * One dataset inside a segment.
 *
 * Carried on the segment read so the detail screen can show what is in the
 * segment without a second round trip against the dataset list.
 *
 * `sheetName` is here for the same reason it is on `DatasetSummary`: a workbook
 * produces one dataset per sheet, so two members can share a source file name
 * and nothing else would say which tab each came from.
 *
 * `contactCount` is counted live from `contacts`, not read from
 * `datasets.imported_count`, for exactly the reason the segment counts are
 * computed: the moment a dataset is re-imported a stored number is a lie.
 *
 * `status` is included because it is the honest explanation for a member that
 * contributes nothing: a dataset still `pending` or `processing` has no
 * contacts yet, and a segment showing "0" beside it should be able to say why.
 */
export const segmentDatasetSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Sheet the dataset reads, as authored. Null for CSV. */
  sheetName: z.string().nullable(),
  status: datasetStatusSchema,
  /** Contacts currently in this dataset. Counted, never cached. */
  contactCount: z.number().int().min(0),
});
export type SegmentDataset = z.infer<typeof segmentDatasetSchema>;

export const segmentSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /**
   * `static` or `cohort`. The discriminator, and the field a screen branches on.
   *
   * Branch on THIS, never on `datasets.length`: a cohort's `datasets` is `[]`
   * and so is a brand-new static segment's, and those two are not the same
   * thing. One holds people and no uploads; the other is a container somebody is
   * about to fill.
   */
  type: segmentTypeSchema,
  description: z.string().nullable(),
  /**
   * The datasets this segment is the union of. Empty is a valid static segment,
   * and **always** empty for a cohort — a cohort may never be given datasets,
   * refused by the API on `datasetIds` and by the database's composite foreign
   * key underneath it.
   */
  datasets: z.array(segmentDatasetSchema),
  counts: segmentCountsSchema,
  /** Which count produced this cohort, and when. `null` for a static segment. */
  cohort: segmentCohortSchema.nullable(),
  /**
   * The rule tree of a `rule` segment; `null` for the other two kinds — the
   * database pairs them both ways (`segments_rules_pairs_with_type`).
   */
  rules: ruleTreeSchema.nullable(),
  /** When a rule segment's members were last brought into line with its rules. Null otherwise, and before the first refresh. */
  rulesRefreshedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Segment = z.infer<typeof segmentSchema>;

export const segmentResponseSchema = z.object({ segment: segmentSchema });
export type SegmentResponse = z.infer<typeof segmentResponseSchema>;

/**
 * Row shape for the segments list.
 *
 * The counts are on the summary rather than only on the detail: the list is
 * exactly where "1,287 contacts / 1,278 recipients" has to be visible, and
 * hiding it behind a click is how a customer ends up surprised by the send
 * volume. They are one grouped aggregate for the whole page, not one query per
 * row. The member datasets themselves are left out — `datasetCount` is all the
 * list needs.
 */
export const segmentSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: segmentTypeSchema,
  description: z.string().nullable(),
  /**
   * How many datasets are combined. Zero is valid, and it is `0` for every
   * cohort — a real zero, not a missing measurement: a cohort combines no
   * uploads, it holds people.
   */
  datasetCount: z.number().int().min(0),
  counts: segmentCountsSchema,
  /** Which count produced this cohort, and when. `null` for a static segment. */
  cohort: segmentCohortSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type SegmentSummary = z.infer<typeof segmentSummarySchema>;

export const SEGMENT_SORT_VALUES = ['createdAt', 'name'] as const;
export const segmentSortSchema = z.enum(SEGMENT_SORT_VALUES);
export type SegmentSort = z.infer<typeof segmentSortSchema>;

export const segmentListQuerySchema = paginationQuerySchema.extend({
  /** Matches the segment name, case-insensitive. */
  search: z.string().trim().max(120).optional(),
  sort: segmentSortSchema.default('createdAt'),
  direction: sortDirectionSchema.default('desc'),
});
export type SegmentListQuery = z.infer<typeof segmentListQuerySchema>;

export const segmentListResponseSchema = paginated(segmentSummarySchema);
export type SegmentListResponse = z.infer<typeof segmentListResponseSchema>;

export const segmentParamsSchema = z.object({ segmentId: uuidSchema });
export type SegmentParams = z.infer<typeof segmentParamsSchema>;

/* -------------------------------------------------------------------------- */
/* Write                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Create a segment.
 *
 * `datasetIds` is optional and defaults to empty — creating the segment and
 * choosing its lists can be one step or two, and an empty segment is a valid
 * thing to look at (it reports zeros).
 *
 * Every id must belong to the calling tenant. That is not something a zod schema
 * can check, and it is not left to the API alone either: the composite foreign
 * key on `segment_datasets (dataset_id, tenant_id)` refuses a borrowed dataset
 * at the database, because a foreign key check is not subject to RLS.
 */
export const createSegmentRequestSchema = z.object({
  name: segmentNameSchema,
  description: segmentDescriptionSchema.optional().default(null),
  datasetIds: segmentDatasetIdsSchema.optional().default([]),
});
export type CreateSegmentRequest = z.infer<typeof createSegmentRequestSchema>;

/**
 * Rename a segment, re-describe it, or change what is in it — any subset, one
 * endpoint.
 *
 * `datasetIds` is a **replacement, not a patch**: the segment ends up being the
 * union of exactly the ids given. Omitting the field leaves membership alone;
 * sending `[]` empties the segment. There is deliberately no add/remove pair,
 * because the picker that drives this is a set of checkboxes and a set is what
 * it should send.
 *
 * `description: null` clears it; omitting it leaves it as it was.
 *
 * **`datasetIds` on a COHORT is refused**, with a field error on `datasetIds`
 * carrying `COHORT_REFUSALS.cohort_takes_no_datasets`. It is refused twice over:
 * `segment_datasets` carries a `segment_type` column pinned to `'static'` by a
 * CHECK and joined into `segments (id, tenant_id, type)` by a composite foreign
 * key, so the database answers 23503 — and the API turns that into a sentence
 * first, the same move `assertDatasetsAreOurs` already makes for a borrowed
 * dataset id. Renaming and re-describing a cohort are ordinary and unchanged, and
 * so is deleting one. There is no way to add or remove one PERSON: a hand-edited
 * cohort is no longer the population any number on the Overview described.
 */
export const updateSegmentRequestSchema = z
  .object({
    name: segmentNameSchema.optional(),
    description: segmentDescriptionSchema.optional(),
    datasetIds: segmentDatasetIdsSchema.optional(),
    /**
     * Slice 67: a rule segment's new rules — a replacement, and the refresh runs
     * in the same request (cause `saved`). Refused on a static segment or a
     * cohort: a kind is chosen at creation and never changed.
     */
    rules: ruleTreeSchema.optional(),
  })
  .refine(
    (body) =>
      body.name !== undefined ||
      body.description !== undefined ||
      body.datasetIds !== undefined ||
      body.rules !== undefined,
    'Nothing to update: send a name, a description, datasetIds or rules',
  );
export type UpdateSegmentRequest = z.infer<typeof updateSegmentRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Saving a counted cohort — slice 50                                          */
/* -------------------------------------------------------------------------- */

/**
 * `POST /overview/opportunities/:kind/cohort` — turn what a tile counted into a
 * segment.
 *
 * The body carries at most a name. **Nothing else, and deliberately no count and
 * no list of people**: a request that named its own population would be a second
 * definition of the audience, and the whole point of this endpoint is that the
 * membership is written by the same SQL that produced the figure on screen. The
 * `kind` is a path parameter and is validated by `saveableOpportunityKindSchema`.
 *
 * `name` is optional and reuses {@link segmentNameSchema}, so a blank or overlong
 * name is refused by the shape every other segment name goes through. Omitted, the
 * API stamps `defaultCohortName(kind, countedAt)` — the kind's label and the date
 * it was counted. Two cohorts MAY share a name: `segments.name` is deliberately
 * not unique per tenant, and a constraint there would turn two people calling
 * their segment "Diwali" into a 23505 nobody can act on.
 *
 * The endpoint lives on the OVERVIEW rather than on segments because the
 * definition of the population lives there. Moving it would mean re-implementing
 * that read in a second module, which is the defect this slice exists to remove.
 */
export const saveCohortRequestSchema = z
  .object({
    name: segmentNameSchema.optional(),
  })
  .strict();
export type SaveCohortRequest = z.infer<typeof saveCohortRequestSchema>;

/**
 * The saved cohort, and whether this request is what created it.
 *
 * **`created` is never rendered.** It exists for the log line and the verify
 * suite. A flag a screen interprets is a sentence waiting to be written, and the
 * standing UI rule says there is no new sentence here.
 *
 * `created: false` is the idempotent path, and it is a deliberate departure from
 * treating a second save as an error: the tile's button saves the cohort and then
 * opens the campaign form, so somebody who clicks, abandons the form and clicks
 * again must land back on the SAME segment rather than on a 409. A save whose
 * population has CHANGED since is not this case — it is a different audience, it
 * gets a new segment, and the date in its default name is what distinguishes the
 * two.
 */
export const saveCohortResponseSchema = z
  .object({
    segment: segmentSchema,
    /** False when an identical population was already saved and is being returned. */
    created: z.boolean(),
  })
  .strict();
export type SaveCohortResponse = z.infer<typeof saveCohortResponseSchema>;

/**
 * `:kind` on the save route — the WHOLE opportunity vocabulary, not the saveable
 * three.
 *
 * A word that is not an opportunity kind at all is a 400 from this shape. But
 * `unused_template` and `segment_never_sent` ARE opportunity kinds, and narrowing
 * the shape to three would answer them with a zod message about an enum — which
 * tells a reader which strings the server likes and nothing about why theirs was
 * refused. They are refused one layer in instead, with
 * `COHORT_REFUSALS.not_an_audience`, which names what the count was OF: *"a count
 * of templates, not of people"*. A defined sentence nobody can reach is the same
 * defect as no sentence at all.
 *
 * The card only draws the save control on the three audience tiles, so this is a
 * backstop either way. The database is the third: 0037's
 * `segments_cohort_kind_is_an_audience` admits the same three labels.
 */
export const saveCohortParamsSchema = z.object({ kind: opportunityKindSchema }).strict();
export type SaveCohortParams = z.infer<typeof saveCohortParamsSchema>;

/* -------------------------------------------------------------------------- */
/* Contact preview                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The preview of who is in a segment has no contract of its own on purpose: it
 * is a page of the same `Contact` rows the dataset screens already show, so it
 * takes `paginationQuerySchema` and answers with `contactListResponseSchema`
 * from `./contact.js`. Re-declaring either here would be two shapes to keep in
 * step for no gain.
 *
 * The preview lists **rows**, not de-duplicated recipients — it is "show me the
 * data", and `counts.uniqueRecipients` is the answer to "how many messages".
 *
 * It works on a cohort unchanged, and that is not free: the API's membership
 * test is one shared predicate covering both kinds, so previewing a cohort shows
 * its people rather than an empty page beside a count of 1,284 — which would
 * have been this slice's worst silent failure.
 */
export const segmentContactPreviewQuerySchema = paginationQuerySchema;
export type SegmentContactPreviewQuery = z.infer<typeof segmentContactPreviewQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Rule segments — slice 67                                                    */
/* -------------------------------------------------------------------------- */

/**
 * `POST /segments` with `type: 'rule'`. The rules are refreshed into members in
 * the same request (cause `saved`), so the segment page opens on real counts.
 *
 * The API parses a create body with {@link createAnySegmentRequestSchema}: this
 * arm first, because it is strict and carries the literal `type`, then slice 2's
 * static create, which has no `type` field at all. A cohort is never created
 * here — only from the Overview.
 */
export const createRuleSegmentRequestSchema = z
  .object({
    type: z.literal('rule'),
    name: segmentNameSchema,
    description: segmentDescriptionSchema.optional().default(null),
    rules: ruleTreeSchema,
  })
  .strict();
export type CreateRuleSegmentRequest = z.infer<typeof createRuleSegmentRequestSchema>;

export const createAnySegmentRequestSchema = z.union([createRuleSegmentRequestSchema, createSegmentRequestSchema]);
export type CreateAnySegmentRequest = z.infer<typeof createAnySegmentRequestSchema>;

/**
 * `POST /segments/preview` — the builder's live counts, without saving.
 *
 * Debounced by the builder; computed by the same compiler the refresher uses,
 * so the figure under "Save" is the membership the save will write.
 */
export const segmentPreviewRequestSchema = z.object({ rules: ruleTreeSchema }).strict();
export type SegmentPreviewRequest = z.infer<typeof segmentPreviewRequestSchema>;

export const segmentPreviewCountSchema = z
  .object({
    id: ruleIdSchema,
    /** People matching this rule (or group) on its own, ignoring its siblings. */
    count: z.number().int().min(0),
  })
  .strict();
export type SegmentPreviewCount = z.infer<typeof segmentPreviewCountSchema>;

export const segmentPreviewResponseSchema = z
  .object({
    /** People the whole tree matches. */
    people: z.number().int().min(0),
    /** Distinct email addresses those people carry — what an email campaign would reach. */
    emailReachable: z.number().int().min(0),
    /** Distinct numbers over each person's representative row — what a WhatsApp send would use. */
    phoneReachable: z.number().int().min(0),
    perRule: z.array(segmentPreviewCountSchema),
    perGroup: z.array(segmentPreviewCountSchema),
    generatedAt: isoDateTimeSchema,
  })
  .strict();
export type SegmentPreviewResponse = z.infer<typeof segmentPreviewResponseSchema>;

/* ---- the membership timeline ---------------------------------------------- */

export const SEGMENT_MEMBERSHIP_EVENT_KIND_VALUES = ['entered', 'left'] as const;
export const segmentMembershipEventKindSchema = z.enum(SEGMENT_MEMBERSHIP_EVENT_KIND_VALUES);
export type SegmentMembershipEventKind = z.infer<typeof segmentMembershipEventKindSchema>;

/**
 * Why a refresh ran. Mirrors the CHECK in 0051, word for word.
 *
 *   saved  — the rules were created or changed
 *   upload — an upload finished importing
 *   sync   — a connected source synced (reserved for data sources)
 *   lead   — a Reach lead landed
 *   event  — a reply, a click or an outcome (debounced, at most once a minute)
 *   hourly — the hourly sweep that catches "in the last 30 days" moving on
 *   manual — somebody pressed refresh, or a campaign or flow refreshed it first
 */
export const SEGMENT_MEMBERSHIP_CAUSE_VALUES = ['saved', 'upload', 'sync', 'lead', 'event', 'hourly', 'manual'] as const;
export const segmentMembershipCauseSchema = z.enum(SEGMENT_MEMBERSHIP_CAUSE_VALUES);
export type SegmentMembershipCause = z.infer<typeof segmentMembershipCauseSchema>;

export const SEGMENT_MEMBERSHIP_CAUSE_LABEL: Readonly<Record<SegmentMembershipCause, string>> = {
  saved: 'Rules saved',
  upload: 'Upload',
  sync: 'Sync',
  lead: 'Reach lead',
  event: 'Activity',
  hourly: 'Hourly check',
  manual: 'Refreshed',
};

export const SEGMENT_MEMBERSHIP_EVENTS_MAX = 200;

export const segmentMembershipEventsQuerySchema = z
  .object({ limit: z.coerce.number().int().min(1).max(SEGMENT_MEMBERSHIP_EVENTS_MAX).default(50) })
  .strict();
export type SegmentMembershipEventsQuery = z.infer<typeof segmentMembershipEventsQuerySchema>;

/**
 * A person as a timeline or a list shows them: a name and ONE masked address.
 * Never a full address — `maskEmail` / `maskPhone` in `./fields.js`. Reused by
 * the flow enrolment lists and the Reach leads page.
 */
export const maskedPersonSchema = z
  .object({
    id: uuidSchema,
    name: z.string().nullable(),
    /** `a***@domain.in` or `+91 ••••• ••123`; null when the person has neither on a live row. */
    address: z.string().nullable(),
  })
  .strict();
export type MaskedPerson = z.infer<typeof maskedPersonSchema>;

export const segmentMembershipEventSchema = z
  .object({
    id: uuidSchema,
    kind: segmentMembershipEventKindSchema,
    cause: segmentMembershipCauseSchema,
    at: isoDateTimeSchema,
    person: maskedPersonSchema,
  })
  .strict();
export type SegmentMembershipEvent = z.infer<typeof segmentMembershipEventSchema>;

/** `GET /segments/:segmentId/membership-events?limit=` — newest first. */
export const segmentMembershipEventsResponseSchema = z
  .object({ events: z.array(segmentMembershipEventSchema).max(SEGMENT_MEMBERSHIP_EVENTS_MAX) })
  .strict();
export type SegmentMembershipEventsResponse = z.infer<typeof segmentMembershipEventsResponseSchema>;
